import {
  type Account,
  AccountsApi,
  type AccountUpdateInput,
  type AwsOrganization,
  type CloudAccountConfiguration,
  type Configuration,
  type ConnectAwsOrganizationRequest,
  type CreateAccountInput,
  CreateProjectRestoreAccountApi,
  CreateProjectSourceAccountApi,
  CreateVaultAccountApi,
  GetAWSStackDetailsApi,
  GetRestoreCloudAccountConfigurationApi,
  GetRestoreCloudAccountConfigurationsApi,
  ListProjectRestoreAccountsApi,
  ListProjectSourceAccountsApi,
  ListScanningAccountsApi,
  ListVaultAccountsApi,
  UpdateProjectRestoreAccountApi,
  UpdateRestoreCloudAccountConfigurationApi,
  UpdateSourceAccountApi,
  GetProjectSourceAccountConfigurationApi,
  GetRestoreCloudAccountTagsConfigurationApi,
  UpdateRestoreCloudAccountTagsConfigurationApi,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/accounts';

export const cloudAccounts = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listSourceAccountsApi = new ListProjectSourceAccountsApi(config);
  const createSourceAccountApi = new CreateProjectSourceAccountApi(config);
  const listVaultAccountsApi = new ListVaultAccountsApi(config);
  const createVaultAccountApi = new CreateVaultAccountApi(config);
  const listRestoreAccountsApi = new ListProjectRestoreAccountsApi(config);
  const createRestoreAccountApi = new CreateProjectRestoreAccountApi(config);
  const updateRestoreAccountApi = new UpdateProjectRestoreAccountApi(config);
  const listScanningAccountsApi = new ListScanningAccountsApi(config);
  const accountsApi = new AccountsApi(config);

  const getRestoreCloudAccountConfigurationsApi =
    new GetRestoreCloudAccountConfigurationsApi(config);

  const getRestoreCloudAccountConfigurationApi =
    new GetRestoreCloudAccountConfigurationApi(config);

  const updateRestoreCloudAccountConfigurationApi =
    new UpdateRestoreCloudAccountConfigurationApi(config);

  const getAWSStackDetailsApi = new GetAWSStackDetailsApi(config);
  const updateSourceAccountApi = new UpdateSourceAccountApi(config);
  const getProjectSourceAccountConfigurationApi =
    new GetProjectSourceAccountConfigurationApi(config);

  const getRestoreAccountTagsApi =
    new GetRestoreCloudAccountTagsConfigurationApi(config);
  const setRestoreAccountTagsApi =
    new UpdateRestoreCloudAccountTagsConfigurationApi(config);

  return {
    source: {
      create: (account: CreateAccountInput) =>
        createSourceAccountApi.createProjectSourceAccount(
          currentProjectId,
          account
        ),
      disconnect: (id: string) =>
        accountsApi.disconnectSourceAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectSourceAccount(currentProjectId, id),
      list: (projectId?: string) =>
        httpClient.execute(
          [baseUrl, '/source-accounts', projectId || currentProjectId],
          async () => {
            if (!projectId && !currentProjectId) {
              return;
            }
            return listSourceAccountsApi.listProjectSourceAccounts(
              projectId || currentProjectId
            );
          }
        ),
      listForProjects: (projectIds: string[]) =>
        httpClient.execute(
          [baseUrl, '/source-accounts-multiple', projectIds],
          async () =>
            Object.fromEntries<Account[]>(
              await Promise.all(
                projectIds.map((id) =>
                  listSourceAccountsApi
                    .listProjectSourceAccounts(id)
                    .then((x) => [id, x.accounts] as [string, Account[]])
                )
              )
            )
        ),
      update: (accountId: string, accountUpdateInput: AccountUpdateInput) => {
        return updateSourceAccountApi.updateSourceAccount(
          accountId,
          accountUpdateInput
        );
      },
      config: {
        get: (accountId: string) =>
          httpClient.execute(
            [baseUrl, '/source-account-config', accountId, currentProjectId],
            async () => {
              if (!accountId) {
                return;
              }
              return getProjectSourceAccountConfigurationApi.getProjectSourceAccountConfiguration(
                accountId,
                currentProjectId
              );
            }
          ),
      },
      metrics: {
        get: (accountId: string) => {
          return httpClient.execute(
            [
              baseUrl,
              '/restore-account-job-metrics',
              accountId,
              currentProjectId,
            ],
            () =>
              accountsApi.getSourceAccountMetricsConfig(
                accountId,
                currentProjectId
              )
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableSourceAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableSourceAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
    },
    vault: {
      create: (account: CreateAccountInput) =>
        createVaultAccountApi.createVaultAccount(account),
      list: () =>
        httpClient.execute([baseUrl, '/vault-accounts'], () =>
          listVaultAccountsApi.listVaultAccounts()
        ),
    },
    restore: {
      create: (account: CreateAccountInput) =>
        createRestoreAccountApi.createProjectRestoreAccount(
          currentProjectId,
          account
        ),
      update: (accountId: string, account: AccountUpdateInput) =>
        updateRestoreAccountApi.updateProjectRestoreAccount(
          accountId,
          currentProjectId,
          account
        ),
      disconnect: (id: string) =>
        accountsApi.disconnectRestoreAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectRestoreAccount(currentProjectId, id),
      list: () =>
        httpClient.execute(
          [baseUrl, '/restore-accounts', currentProjectId],
          () =>
            listRestoreAccountsApi.listProjectRestoreAccounts(currentProjectId)
        ),
      configuration: {
        list: () =>
          httpClient.execute(
            [
              baseUrl,
              '/restore-accounts',
              currentProjectId,
              'cloud-account-configuration',
            ],
            () =>
              getRestoreCloudAccountConfigurationsApi.getRestoreCloudAccountConfigurations(
                currentProjectId
              )
          ),
        get: (accountId: string) =>
          getRestoreCloudAccountConfigurationApi.getRestoreCloudAccountConfiguration(
            accountId,
            currentProjectId
          ),
        update: (
          accountId: string,
          cloudAccountConfiguration: CloudAccountConfiguration
        ) =>
          updateRestoreCloudAccountConfigurationApi.updateRestoreCloudAccountConfiguration(
            accountId,
            currentProjectId,
            cloudAccountConfiguration
          ),
        tags: {
          get: (accountId: string) =>
            httpClient.execute(
              [baseUrl, '/restore-account-tags', accountId, currentProjectId],
              () =>
                getRestoreAccountTagsApi.getRestoreCloudAccountTagsConfiguration(
                  accountId,
                  currentProjectId
                )
            ),
          set: (accountId: string, tags: Record<string, string>) =>
            setRestoreAccountTagsApi.updateRestoreCloudAccountTagsConfiguration(
              accountId,
              currentProjectId,
              Object.entries(tags).map(([key, value]) => ({
                key,
                value,
              }))
            ),
        },
      },
      metrics: {
        get: (accountId: string) => {
          return httpClient.execute(
            [
              baseUrl,
              '/restore-account-job-metrics',
              accountId,
              currentProjectId,
            ],
            () =>
              accountsApi.getRestoreAccountMetricsConfig(
                accountId,
                currentProjectId
              )
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
    },
    scanning: {
      create: () => {
        throw new Error('Not implemented');
      },
      list: () =>
        httpClient.execute([baseUrl, '/scanning-accounts'], () =>
          listScanningAccountsApi.listScanningAccounts()
        ),
      listAsync: () => listScanningAccountsApi.listScanningAccounts(),
    },
    managementGroups: {
      list: () =>
        httpClient.execute(
          [baseUrl, '/management-groups', currentProjectId],
          () => accountsApi.listAzureManagementGroups(currentProjectId)
        ),
      activate: (managementGroupId: string) =>
        accountsApi.activateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
      deactivate: (managementGroupId: string) =>
        accountsApi.deactivateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
    },
    organization: {
      listForProjects: (projectIds: string[]) =>
        httpClient.execute(
          [baseUrl, '/aws-organizations', projectIds],
          async () =>
            Object.fromEntries<AwsOrganization[]>(
              await Promise.all(
                projectIds.map((id) =>
                  accountsApi
                    .listAwsOrganizations(id)
                    .then(
                      (x) =>
                        [id, x.organizations] as [string, AwsOrganization[]]
                    )
                )
              )
            )
        ),
      connect: (connectAwsOrganizationRequest: ConnectAwsOrganizationRequest) =>
        accountsApi.connectAwsOrganization(
          currentProjectId,
          connectAwsOrganizationRequest
        ),
      activate: (organizationId: string) =>
        accountsApi.activateAwsOrganization(currentProjectId, organizationId),
      deactivate: (organizationId: string) =>
        accountsApi.deactivateAwsOrganization(currentProjectId, organizationId),
    },
    eksClusters: {
      list: () =>
        httpClient.execute([baseUrl, '/eks-clusters', currentProjectId], () =>
          accountsApi.listEksClusters(currentProjectId)
        ),
    },
    stack: {
      get: (accountId: string) =>
        getAWSStackDetailsApi.getAWSStackDetails(currentProjectId, accountId),
    },
  };
};
